<template>
  <section
    ref="sectionRef"
    class="home-kpis"
    :class="{ 'is-visible': hasEnteredViewport }"
    aria-label="Chiffres clés de l'Institut Taxi"
  >
    <div class="container">
      <div class="home-kpis__panel">
        <h2 class="home-kpis__sr-only">Chiffres clés de l'Institut Taxi</h2>

        <div class="home-kpis__meta">
          <p class="home-kpis__tag" style="color: #002a54">
            L'Institut Taxi en chiffres
          </p>
          <p class="home-kpis__update">Mis à jour le {{ updatedAt }}</p>
        </div>

        <div class="home-kpis__grid">
          <article
            v-for="(item, index) in items"
            :key="`${item.label}-${index}`"
            class="home-kpis__item"
          >
            <div class="home-kpis__icon" aria-hidden="true">
              <i :class="item.icon"></i>
            </div>

            <div class="home-kpis__body">
              <p class="home-kpis__value">
                <span aria-hidden="true">
                  {{ formatDisplayValue(item, index) }}
                </span>
                <span class="home-kpis__sr-only">
                  {{ formatFinalValue(item) }}
                </span>
              </p>
              <p class="home-kpis__label">{{ item.label }}</p>
              <a
                v-if="item.link"
                :href="item.link.href"
                class="home-kpis__link"
                target="_blank"
                rel="noopener noreferrer"
              >
                {{ item.link.label }}
              </a>
            </div>
          </article>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
const cloneItems = (items) =>
  items.map((item) => ({
    ...item,
    link: item.link ? { ...item.link } : null,
  }));

const defaultItems = [
  {
    value: 2000,
    prefix: "+",
    label: "stagiaires formés",
    icon: "fa fa-users",
  },
  {
    value: 91,
    suffix: "%",
    label: "de réussite",
    icon: "fa fa-line-chart",
  },
  {
    value: 1017,
    label: "avis Google",
    icon: "fa fa-star",
    link: {
      href: "https://www.google.com/search?q=Institut+Taxi+Gennevilliers+avis+Google",
      label: "Voir les avis",
    },
  },
];

export default {
  name: "HomeKpis",
  props: {
    items: {
      type: Array,
      default: () => cloneItems(defaultItems),
    },
    updatedAt: {
      type: String,
      default: "24 mars 2026",
    },
  },
  data() {
    const prefersReducedMotion =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    return {
      animationFrameId: null,
      hasAnimated: prefersReducedMotion,
      hasEnteredViewport: prefersReducedMotion,
      observer: null,
      prefersReducedMotion,
      reducedMotionMediaQuery: null,
      displayValues: this.items.map((item) =>
        prefersReducedMotion ? item.value : 0
      ),
    };
  },
  mounted() {
    if (typeof window === "undefined") {
      return;
    }

    this.reducedMotionMediaQuery = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    );

    if (this.reducedMotionMediaQuery.addEventListener) {
      this.reducedMotionMediaQuery.addEventListener(
        "change",
        this.handleReducedMotionChange
      );
    } else if (this.reducedMotionMediaQuery.addListener) {
      this.reducedMotionMediaQuery.addListener(this.handleReducedMotionChange);
    }

    if (this.prefersReducedMotion) {
      this.finishAnimation();
      return;
    }

    this.setupObserver();
  },
  beforeUnmount() {
    if (this.observer) {
      this.observer.disconnect();
    }

    if (this.animationFrameId) {
      window.cancelAnimationFrame(this.animationFrameId);
    }

    if (!this.reducedMotionMediaQuery) {
      return;
    }

    if (this.reducedMotionMediaQuery.removeEventListener) {
      this.reducedMotionMediaQuery.removeEventListener(
        "change",
        this.handleReducedMotionChange
      );
    } else if (this.reducedMotionMediaQuery.removeListener) {
      this.reducedMotionMediaQuery.removeListener(
        this.handleReducedMotionChange
      );
    }
  },
  methods: {
    easeOutCubic(progress) {
      return 1 - Math.pow(1 - progress, 3);
    },
    formatDisplayValue(item, index) {
      return `${item.prefix || ""}${this.displayValues[index]}${
        item.suffix || ""
      }`;
    },
    formatFinalValue(item) {
      return `${item.prefix || ""}${item.value}${item.suffix || ""}`;
    },
    handleReducedMotionChange(event) {
      this.prefersReducedMotion = event.matches;

      if (event.matches) {
        this.finishAnimation();
        return;
      }

      if (!this.hasAnimated) {
        this.setupObserver();
      }
    },
    finishAnimation() {
      this.hasAnimated = true;
      this.hasEnteredViewport = true;
      this.displayValues = this.items.map((item) => item.value);

      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }

      if (this.animationFrameId) {
        window.cancelAnimationFrame(this.animationFrameId);
        this.animationFrameId = null;
      }
    },
    setupObserver() {
      if (this.hasAnimated) {
        return;
      }

      if (!("IntersectionObserver" in window)) {
        this.startAnimation();
        return;
      }

      if (this.observer) {
        this.observer.disconnect();
      }

      this.observer = new IntersectionObserver(
        (entries) => {
          const [entry] = entries;

          if (entry.isIntersecting) {
            this.startAnimation();
          }
        },
        {
          threshold: 0.3,
        }
      );

      this.observer.observe(this.$refs.sectionRef);
    },
    startAnimation() {
      if (this.hasAnimated) {
        return;
      }

      this.hasEnteredViewport = true;

      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }

      const duration = 1200;
      const stagger = 120;
      const animationStart = window.performance.now();

      const animate = (currentTime) => {
        let completedItems = 0;

        this.displayValues = this.items.map((item, index) => {
          const itemStart = animationStart + index * stagger;
          const itemProgress = Math.min(
            Math.max((currentTime - itemStart) / duration, 0),
            1
          );

          if (itemProgress === 1) {
            completedItems += 1;
          }

          return Math.round(item.value * this.easeOutCubic(itemProgress));
        });

        if (completedItems === this.items.length) {
          this.finishAnimation();
          return;
        }

        this.animationFrameId = window.requestAnimationFrame(animate);
      };

      this.animationFrameId = window.requestAnimationFrame(animate);
    },
  },
};
</script>

<style scoped lang="scss">
.home-kpis {
  padding: 22px 12px 24px;

  @media screen and (min-width: 992px) {
    padding: 28px 0 30px;
  }

  &__panel {
    background: linear-gradient(
      135deg,
      rgba(255, 255, 255, 0.98) 0%,
      rgba(245, 236, 236, 0.92) 100%
    );
    border: 1px solid rgba(0, 42, 84, 0.08);
    border-radius: 30px;
    box-shadow: rgba(0, 42, 84, 0.08) 0px 18px 45px;
    opacity: 0;
    overflow: hidden;
    padding: 24px 20px;
    position: relative;
    transform: translateY(24px);
    transition: opacity 0.5s ease, transform 0.5s ease;

    @media screen and (min-width: 992px) {
      padding: 26px 30px 24px;
    }

    &::before {
      background: $color-primary;
      content: "";
      height: 4px;
      inset: 0 0 auto 0;
      position: absolute;
    }
  }

  &.is-visible &__panel {
    opacity: 1;
    transform: translateY(0);
  }

  &__meta {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-bottom: 18px;
    padding-top: 8px;

    @media screen and (min-width: 768px) {
      align-items: center;
      flex-direction: row;
      justify-content: space-between;
    }
  }

  &__tag {
    color: $color-secondary;
    font-family: "Spartan", "Arial Black", Arial, sans-serif;
    font-weight: 700;
    font-size: clamp(1.2rem, 3vw, 1.8rem);
    margin: 0;
  }

  &__update {
    color: $color-text-grey;
    font-size: 0.95rem;
    margin: 0;
  }

  &__grid {
    display: grid;
    gap: 0;

    @media screen and (min-width: 992px) {
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }
  }

  &__item {
    align-items: flex-start;
    border-top: 1px solid rgba(0, 42, 84, 0.08);
    display: flex;
    gap: 16px;
    opacity: 0;
    padding: 20px 0;
    transform: translateY(18px);
    transition: opacity 0.45s ease, transform 0.45s ease;

    &:first-child {
      border-top: 0;
      padding-top: 6px;
    }

    @media screen and (min-width: 992px) {
      border-top: 0;
      min-height: 172px;
      padding: 12px 28px 0;

      &:first-child {
        padding-left: 0;
        padding-top: 12px;
      }

      &:not(:first-child) {
        border-left: 1px solid rgba(0, 42, 84, 0.08);
      }

      &:last-child {
        padding-right: 0;
      }
    }
  }

  &.is-visible &__item {
    opacity: 1;
    transform: translateY(0);
  }

  &.is-visible &__item:nth-child(2) {
    transition-delay: 0.08s;
  }

  &.is-visible &__item:nth-child(3) {
    transition-delay: 0.16s;
  }

  &__icon {
    align-items: center;
    background-color: $color-primary;
    border-radius: 18px;
    box-shadow: rgba(0, 42, 84, 0.18) 0px 12px 24px;
    color: $color-secondary;
    display: flex;
    flex: 0 0 58px;
    font-size: 1.25rem;
    height: 58px;
    justify-content: center;
    width: 58px;
  }

  &__item:nth-child(2) &__icon {
    background-color: #feb914;
    color: $color-primary;
  }

  &__body {
    display: flex;
    flex: 1;
    flex-direction: column;
    min-height: 58px;
  }

  &__value {
    color: $color-secondary;
    font-family: "Spartan", "Arial Black", Arial, sans-serif;
    font-weight: 700;
    font-size: clamp(2.2rem, 4vw, 3.3rem);
    line-height: 1;
    margin-bottom: 10px;
  }

  &__item:nth-child(2) &__value {
    color: $color-primary;
  }

  &__label {
    color: $color-text;
    font-size: 1.05rem;
    margin-bottom: 0;

    @media screen and (min-width: 992px) {
      font-size: 1.15rem;
    }
  }

  &__link {
    color: $color-secondary;
    font-size: 0.7rem;
    font-weight: 700;
    line-height: 1.15;
    margin-top: 12px;
    text-decoration: none;
    width: fit-content;

    &:hover {
      color: $color-primary;
    }
  }

  &__sr-only {
    border: 0;
    clip: rect(0 0 0 0);
    height: 1px;
    margin: -1px;
    overflow: hidden;
    padding: 0;
    position: absolute;
    white-space: nowrap;
    width: 1px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .home-kpis {
    &__panel,
    &__item {
      opacity: 1;
      transform: none;
      transition: none;
    }
  }
}
</style>
